import { useContext } from "react";
import type { AxiosResponse } from "axios";

import type {
  S132Record,
  WithdrawS132AttemptRequest,
} from "@thalamos/aspire-common";
import { s132AttemptChain } from "@thalamos/aspire-common";

import type { S132AttemptSubmission } from "./RecordS132AttemptDialog.js";
import { S132HistoryDialog } from "./S132HistoryDialog.js";
import { api, apiHooks } from "~/api.js";
import { LoggedInUserContext } from "~/Contexts.js";
import { assertOkResponse } from "~/helpers/assertOkResponse.js";

/**
 * A refusal surfaces the server's message (see `assertOkResponse`); a request
 * that rejects (no response, or a 5xx) surfaces `fallback`.
 */
async function sendS132Change(
  send: () => Promise<AxiosResponse<S132Record>>,
  fallback: string,
): Promise<S132Record> {
  let response: AxiosResponse<S132Record>;
  try {
    response = await send();
  } catch {
    throw new Error(fallback);
  }
  return assertOkResponse(response).data;
}

export function saveS132Attempt(
  patientId: string,
  { request, correction }: S132AttemptSubmission,
): Promise<S132Record> {
  return sendS132Change(
    () =>
      correction
        ? api.s132.correctAttempt(patientId, correction.attemptId, {
            ...request,
            correctionReason: correction.correctionReason,
          })
        : api.s132.recordAttempt(patientId, request),
    "The attempt could not be saved. Try again.",
  );
}

/** Offered to users as deleting the entry, as `WithdrawS132AttemptDialog` describes. */
export function withdrawS132Attempt(
  patientId: string,
  attemptId: string,
  request: WithdrawS132AttemptRequest,
): Promise<S132Record> {
  return sendS132Change(
    () => api.s132.withdrawAttempt(patientId, attemptId, request),
    "The entry could not be deleted. Try again.",
  );
}

export type S132HistoryDialogContainerProps = {
  patientId: string;
  patientName?: string;
  onClose: () => void;
  onChanged?: (record: S132Record) => void;
};

export function S132HistoryDialogContainer(
  props: S132HistoryDialogContainerProps,
) {
  const userContext = useContext(LoggedInUserContext);
  const [{ data: record, loading, error }] = apiHooks.s132.record(
    props.patientId,
    false,
  );

  const save = (submission: S132AttemptSubmission) =>
    saveS132Attempt(props.patientId, submission);

  return (
    <S132HistoryDialog
      patientName={props.patientName}
      recorderName={userContext?.user.name}
      record={record}
      loading={loading}
      failed={!!error || (!loading && !record)}
      onClose={props.onClose}
      onSave={save}
      canWithdraw={(attempt, record) =>
        userContext?.user.sessionContext?.teamType === "mha" ||
        s132AttemptChain(attempt, record.attempts).some(
          (version) => version.recordedByUserId === userContext?.user.id,
        )
      }
      onWithdraw={(attemptId, request) =>
        withdrawS132Attempt(props.patientId, attemptId, request)
      }
      onChanged={props.onChanged}
    />
  );
}
